import { Controller } from "@hotwired/stimulus"
import { submitChatOnEnter } from "../utils/vpe_chat_message_utils"

const ASSISTANT_RESPONSE_PENDING =
  ".ai-assistant-typing-indicator, .aqui-loading-dots"

const IDLE_PLACEHOLDER = "Ask me anything"
const RESPONDING_PLACEHOLDER = "AI is responding..."

const STALL_SWAL_HTML =
  "<p>Your message may not have sent. If you were offline or the connection dropped, wait until you are back on the internet, then refresh this page to restore the chat and controls.</p>"

export default class extends Controller {
  static targets = ["input", "submitButton", "attachButton"]

  static values = {
    stallModalMs: { type: Number, default: 120000 }, // 2 minutes
  }

  connect() {
    this._submitInFlight = false
    this._holdRespondingUntilSettled = false
    this._pendingFocusAfterTurn = false
    this._httpStallTimer = null
    this._replyScheduleTimer = null
    this._replyStallTimer = null
    this._holdReleaseTimer = null
    this._stallAlertShown = false
    this._defaultPlaceholder = this._idlePlaceholderFrom(this.hasInputTarget ? this.inputTarget : null)
    this._chatObserver = null
    this._chatSyncDebounce = null

    this._onAfterStream = () => this.handleAfterStream()
    this._onSubmitStart = (e) => this.handleSubmitStart(e)
    this._onSubmitEnd = (e) => this.handleSubmitEnd(e)

    document.addEventListener("turbo:after-stream-render", this._onAfterStream)
    document.addEventListener("turbo:submit-start", this._onSubmitStart)
    document.addEventListener("turbo:submit-end", this._onSubmitEnd)
    document.addEventListener("turbo:fetch-request-error", this._onSubmitEnd)

    this._bindChatMutationObserver()
    this.sync()
  }

  disconnect() {
    this._clearHttpStallTimer()
    this._clearReplyStallTimer()
    this._clearReplyScheduleTimer()
    this._clearHoldReleaseTimer()
    this._submitInFlight = false
    this._holdRespondingUntilSettled = false
    this._pendingFocusAfterTurn = false
    this._stallAlertShown = false
    this._chatObserver?.disconnect()
    this._chatObserver = null
    clearTimeout(this._chatSyncDebounce)
    this._chatSyncDebounce = null
    document.removeEventListener("turbo:after-stream-render", this._onAfterStream)
    document.removeEventListener("turbo:submit-start", this._onSubmitStart)
    document.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    document.removeEventListener("turbo:fetch-request-error", this._onSubmitEnd)
  }

  handleSubmitStart(event) {
    const form = this._formFromTurboSubmitEvent(event)
    if (!this._formInScopeLive(form)) return
    this._submitInFlight = true
    this._holdRespondingUntilSettled = true
    this._pendingFocusAfterTurn = true
    this._stallAlertShown = false
    this._clearHttpStallTimer()
    this._clearReplyStallTimer()
    this._clearReplyScheduleTimer()
    this._clearHoldReleaseTimer()
    this._httpStallTimer = window.setTimeout(() => this._maybeShowHttpStallModal(), this.stallModalMsValue)
    this.sync()
  }

  handleSubmitEnd(event) {
    const form = this._formFromTurboSubmitEvent(event)
    if (!this._formWasOurAiAssistantSubmit(form)) return
    this._clearHttpStallTimer()
    this._submitInFlight = false
    // Keep responding placeholder until streams append loading dots (or settle without them).
    this._holdRespondingUntilSettled = true
    this.sync()
    this._scheduleHoldRelease()
    this._scheduleFollowUpSync()
  }

  handleAfterStream() {
    if (this._holdRespondingUntilSettled && this._awaitingAssistantResponse()) {
      this._holdRespondingUntilSettled = false
      this._clearHoldReleaseTimer()
    }
    this.sync()
  }

  _clearHttpStallTimer() {
    if (this._httpStallTimer != null) {
      clearTimeout(this._httpStallTimer)
      this._httpStallTimer = null
    }
  }

  _clearHoldReleaseTimer() {
    if (this._holdReleaseTimer != null) {
      clearTimeout(this._holdReleaseTimer)
      this._holdReleaseTimer = null
    }
  }

  _clearReplyScheduleTimer() {
    if (this._replyScheduleTimer != null) {
      clearTimeout(this._replyScheduleTimer)
      this._replyScheduleTimer = null
    }
  }

  _clearReplyStallTimer() {
    if (this._replyStallTimer != null) {
      clearTimeout(this._replyStallTimer)
      this._replyStallTimer = null
    }
  }

  /** Drop the post-submit hold once loading dots appear, or shortly after if they never do (errors). */
  _scheduleHoldRelease() {
    this._clearHoldReleaseTimer()
    this._holdReleaseTimer = window.setTimeout(() => {
      this._holdReleaseTimer = null
      if (!this._holdRespondingUntilSettled) return
      this._holdRespondingUntilSettled = false
      this.sync()
    }, 150)
  }

  /** No submit-end (hung fetch): still waiting for HTTP completion */
  _maybeShowHttpStallModal() {
    this._httpStallTimer = null
    if (!this.element.isConnected) return
    if (!this._submitInFlight) return
    this._showStallModalOnce()
  }

  /** POST finished but assistant UI still shows typing / loading indicator */
  _maybeShowReplyStallModal() {
    this._replyStallTimer = null
    if (!this.element.isConnected) return
    if (!this._awaitingAssistantResponse()) return
    this._showStallModalOnce()
  }

  _showStallModalOnce() {
    if (this._stallAlertShown) return
    this._stallAlertShown = true

    const payload = {
      icon: "info",
      title: "Taking longer than expected",
      html: STALL_SWAL_HTML,
      showCancelButton: true,
      confirmButtonText: "Refresh page",
      cancelButtonText: "Dismiss",
    }

    if (window.Swal) {
      const result = window.Swal.fire(payload)
      if (result && typeof result.then === "function") {
        result.then((res) => {
          if (res.isConfirmed) window.location.reload()
        })
      }
    } else {
      window.alert(
        "Your message may not have sent. When you are back online, refresh this page to restore the chat.",
      )
    }
  }

  /** After Turbo has delivered the create response, detect assistant typing placeholder and watch for slow reply */
  _ensureReplyStallTimer() {
    if (this._submitInFlight) return
    if (!this._awaitingAssistantResponse()) return
    if (this._stallAlertShown) return
    if (this._replyStallTimer != null || this._replyScheduleTimer != null) return

    this._replyScheduleTimer = window.setTimeout(() => {
      this._replyScheduleTimer = null
      if (!this.element.isConnected) return
      if (this._submitInFlight) return
      if (!this._awaitingAssistantResponse()) return
      if (this._stallAlertShown) return
      this._replyStallTimer = window.setTimeout(() => this._maybeShowReplyStallModal(), this.stallModalMsValue)
    }, 0)
  }

  _formFromTurboSubmitEvent(event) {
    return event.detail?.formSubmission?.formElement || (event.target?.tagName === "FORM" ? event.target : null)
  }

  _formWasOurAiAssistantSubmit(form) {
    if (!form || form.tagName !== "FORM" || !form.action?.includes("/ai_assistant/")) return false
    if (!this._submitInFlight) return false
    return true
  }

  handleInput() {
    this.sync()
  }

  submitOnEnter(event) {
    submitChatOnEnter(event, {
      inputTarget: this.hasInputTarget ? this.inputTarget : null,
      sendButtonTarget: this.hasSubmitButtonTarget ? this.submitButtonTarget : null,
    })
  }

  inputTargetConnected() {
    const idle = this._idlePlaceholderFrom(this.inputTarget)
    if (idle) this._defaultPlaceholder = idle
    this._bindChatMutationObserver()
    this.sync()
    this._maybeFocusInputAfterTurn()
  }

  submitButtonTargetConnected() {
    this.sync()
  }

  attachButtonTargetConnected() {
    this.sync()
  }

  _idlePlaceholderFrom(input) {
    if (!input) return ""
    const raw = input.getAttribute("placeholder") || input.placeholder || ""
    return raw === RESPONDING_PLACEHOLDER ? "" : raw
  }

  _resolvedIdlePlaceholder() {
    return this._defaultPlaceholder || IDLE_PLACEHOLDER
  }

  _formInScopeLive(form) {
    return form?.tagName === "FORM" && form.action?.includes("/ai_assistant/") && this.element.contains(form)
  }

  /** Messages live beside the form under .chatbot-container, not inside the form node */
  _messagesMountEl() {
    return (
      this.element.closest(".chatbot-container")?.querySelector('[id^="thread_messages_"]') ||
      this.element.querySelector('[id^="thread_messages_"]')
    )
  }

  _awaitingAssistantResponse() {
    const messagesEl = this._messagesMountEl()
    return Boolean(messagesEl?.querySelector(ASSISTANT_RESPONSE_PENDING))
  }

  _bindChatMutationObserver() {
    this._chatObserver?.disconnect()
    const messagesEl = this._messagesMountEl()
    if (!messagesEl) return
    this._chatObserver = new MutationObserver(() => this._debouncedChatSync())
    this._chatObserver.observe(messagesEl, { childList: true, subtree: true })
  }

  _debouncedChatSync() {
    clearTimeout(this._chatSyncDebounce)
    this._chatSyncDebounce = setTimeout(() => {
      this._chatSyncDebounce = null
      this.sync()
    }, 100)
  }

  /** Re-sync after submit and next frame so DOM / streams can settle */
  _scheduleFollowUpSync() {
    requestAnimationFrame(() => {
      this.sync()
      requestAnimationFrame(() => this.sync())
    })
  }

  sync() {
    if (!this.hasSubmitButtonTarget) return

    if (!this._awaitingAssistantResponse()) {
      this._clearReplyStallTimer()
      this._clearReplyScheduleTimer()
    } else if (!this._submitInFlight) {
      this._ensureReplyStallTimer()
    }

    const awaiting =
      this._submitInFlight || this._holdRespondingUntilSettled || this._awaitingAssistantResponse()
    const empty = !this.hasInputTarget || this.inputTarget.value.trim() === ""

    if (this.hasInputTarget) {
      this.inputTarget.disabled = awaiting
      this.inputTarget.placeholder = awaiting ? RESPONDING_PLACEHOLDER : this._resolvedIdlePlaceholder()
    }

    this.submitButtonTarget.disabled = empty || awaiting
    if (this.hasAttachButtonTarget) {
      this.attachButtonTarget.disabled = awaiting
    }
    this._maybeFocusInputAfterTurn()
  }

  /** After user submit + assistant reply, return focus to input (VPE chat parity). */
  _maybeFocusInputAfterTurn() {
    if (!this._pendingFocusAfterTurn) return
    if (this._submitInFlight || this._holdRespondingUntilSettled || this._awaitingAssistantResponse()) return
    if (!this.hasInputTarget || this.inputTarget.disabled) return

    this._pendingFocusAfterTurn = false
    requestAnimationFrame(() => {
      if (this.hasInputTarget && this.inputTarget.isConnected && !this.inputTarget.disabled) {
        this.inputTarget.focus()
      }
    })
  }
}